import { useEffect, useState } from 'react';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import {
	COUNTER_LOCALE_ATTR,
	COUNTER_MODE_ATTR,
	COUNTER_MODE_ENTRANCE,
	COUNTER_VALUE_ATTR,
} from '@worldcoo/ui/lib/counter-contract';
import type { Locale } from '@worldcoo/monolith/core';
import { DigitRoll } from './digit-roll';

/**
 * Every cell of every column, and the separators between them, on ONE line box.
 *
 * No size of its own: the figure takes the type of the tile it sits in (`FLUID_FIGURE` scales it off the
 * tile's width), so the cells inherit the font and the line-height and come out exactly as tall as the
 * paragraph's own line — which is both what keeps the tile from resizing and what gives the roll ten
 * cells of equal height to count in.
 */
const CELL = 'block';

/**
 * A live figure as an ODOMETER: one rolling 0–9 column per digit, with the locale's own separators
 * standing still between them.
 *
 * WHY NOT `AnimateNumber`. That one counts THROUGH every intermediate value — it eases a float towards
 * the target and reformats it every frame — which is a fine entrance and the wrong thing for a counter
 * that afterwards moves by one donation at a time: the whole figure is rewritten to advance its last
 * two digits. The roll animates the digits that actually changed and leaves the rest alone, which is
 * what a total ticking up in front of a donor should look like.
 *
 * HYDRATION, which is the reason for `rolled` and not a nicety. The first render must be reproducible on
 * the server, so both sides render the target's digits ZEROED — the target's own shape, so no column is
 * added and nothing reflows once the real digits arrive — and the roll starts after mount, i.e. after
 * React has finished comparing. That is the same bargain `AnimateNumber`'s entrance mode strikes, and it
 * is why this node can keep full mismatch reporting instead of suppressing it; see
 * `counter-contract.ts` for what the attributes below are worth to the client-error reporter.
 *
 * A screen reader gets the real figure, once, from the `sr-only` copy: `DigitRoll` is `aria-hidden`
 * throughout, so the columns contribute no text at all. No `aria-live` — a total that moves with every
 * donation would talk over everything else on the page.
 */
export function RollingFigure(props: {
	locale: Locale;
	value: number;
	decimalDigits: number;
	suffix?: string;
	className?: string;
}) {
	const [rolled, setRolled] = useState(false);

	// Effect and not a layout effect: the zeroed figure has to PAINT before the digits change, or there
	// is no style change for the roll to transition from.
	useEffect(() => setRolled(true), []);

	const figure = formatNumber(props.locale, props.value, props.decimalDigits, { alwaysGroup: true });
	const text = props.suffix ? `${figure} ${props.suffix}` : figure;
	const shown = rolled ? text : text.replace(/\d/g, '0');

	return (
		<span
			className={props.className}
			{...{
				[COUNTER_VALUE_ATTR]: props.value,
				[COUNTER_MODE_ATTR]: COUNTER_MODE_ENTRANCE,
				[COUNTER_LOCALE_ATTR]: props.locale,
			}}
			// A page translator treats a figure as prose and restates it with another locale's separators —
			// a donation total rewritten in front of a donor. See the note in `AnimateNumber`, which carries
			// the same opt-out for the same reason.
			translate="no"
		>
			<span className="sr-only">{text}</span>
			{/* A FLEX row, not inline-blocks on a baseline: a window that clips (`overflow-hidden`) takes its
			    baseline from its bottom edge rather than from its text, so on a baseline the digits would sit
			    lower than the separators between them. As flex items of equal height they line up by
			    construction. */}
			<span aria-hidden className="inline-flex">
				{shown.split('').map((character, index) => {
					// The PLACE, counted from the right: the units digit is what stays put when a total crosses a
					// power of ten and gains a digit, so every column and every separator keeps its own element.
					// Keyed from the left they would all shift by one and the whole figure would roll for a single
					// donation — which is the case the rule below is warning about, in reverse.
					const place = text.length - index;
					return /\d/.test(character) ? (
						// oxlint-disable-next-line react/no-array-index-key
						<DigitRoll key={place} digit={Number(character)} cell={CELL} />
					) : (
						// oxlint-disable-next-line react/no-array-index-key
						<span key={place} className={`${CELL} whitespace-pre`}>
							{character}
						</span>
					);
				})}
			</span>
		</span>
	);
}
